/**
 * The shared state hook behind the layer controls panel. Centralizes
 * present-leaf filtering, group + per-layer visibility (including the
 * app-atom wiring for the always-listed boundary leaves), group + per-layer
 * opacity, and swipe membership.
 */
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import {
  clamp01,
  groupOpacityAtom,
  groupVisibilityAtom,
  leafOpacityAtom,
  leafVisibilityAtom,
  presentLeavesAtom,
  swipeLayersAtom,
} from './atoms';
import { LAYER_REGISTRY, type LayerGroup, type LayerLeaf } from './layerRegistry';
import { viewCountiesAtom } from '../../Incident/CountyLayer';
import { viewTribalBoundariesAtom } from '../layers/TribalLayer';

const EVENT_GROUP = (() => {
  const group = LAYER_REGISTRY.find(g => g.isEvent);
  if (group == null) {
    throw new Error('layer registry has no event group -- the swipe tool has no candidates');
  }
  return group;
})();

export function useLayerState() {
  const [leafVisibility, setLeafVisibility] = useAtom(leafVisibilityAtom);
  const [groupVisibility, setGroupVisibility] = useAtom(groupVisibilityAtom);
  const [groupOpacity, setGroupOpacity] = useAtom(groupOpacityAtom);
  const [leafOpacity, setLeafOpacity] = useAtom(leafOpacityAtom);
  const [swipeLayers, setSwipeLayers] = useAtom(swipeLayersAtom);
  const presentLeaves = useAtomValue(presentLeavesAtom);

  // App-controlled boundary visibility: the panel eye is the SINGLE control
  // for these, so it reads/writes the app's own atoms -- which also
  // mount/unmount the layers -- instead of the layer-controls state. Both
  // default to off.
  const [viewCounties, setViewCounties] = useAtom(viewCountiesAtom);
  const [viewTribal, setViewTribal] = useAtom(viewTribalBoundariesAtom);
  const appVisibility: Record<string, { value: boolean; set: (v: boolean) => void }> = useMemo(
    () => ({
      counties: { value: viewCounties, set: setViewCounties },
      tribal: { value: viewTribal, set: setViewTribal },
    }),
    [viewCounties, setViewCounties, viewTribal, setViewTribal]
  );

  const presentSet = useMemo(() => new Set(presentLeaves), [presentLeaves]);

  /** Groups with their children trimmed to the present leaves -- plus the
   * `alwaysListed` leaves (boundaries), which appear whether or not a matching
   * layer is on the map. Empty groups are dropped. */
  const presentGroups: LayerGroup[] = useMemo(
    () =>
      LAYER_REGISTRY.map(g => ({
        ...g,
        children: g.children.filter(l => l.alwaysListed || presentSet.has(l.key)),
      })).filter(g => g.children.length > 0),
    [presentSet]
  );

  /** Present event leaves only (the swipe target candidates). */
  const presentEventLeaves: LayerLeaf[] = useMemo(
    () => EVENT_GROUP.children.filter(l => presentSet.has(l.key)),
    [presentSet]
  );

  // Bracket lookups on record state guard against inherited keys: leafKey is
  // interpolated into objects whose prototype chain a polluted
  // Object.prototype would otherwise answer for -- and here the looked-up
  // value's `set` gets CALLED.
  const appEntry = useCallback(
    (leafKey: string) =>
      Object.hasOwn(appVisibility, leafKey) ? appVisibility[leafKey] : undefined,
    [appVisibility]
  );

  /** The layer's OWN eye state (its group may still hide it). */
  const isLeafVisible = useCallback(
    (leafKey: string) => appEntry(leafKey)?.value ?? leafVisibility[leafKey] !== false,
    [appEntry, leafVisibility]
  );

  const setLeafVisible = useCallback(
    (leafKey: string, visible: boolean) => {
      const app = appEntry(leafKey);
      if (app != null) {
        app.set(visible);
      } else {
        setLeafVisibility(prev => ({ ...prev, [leafKey]: visible }));
      }
    },
    [appEntry, setLeafVisibility]
  );

  const toggleLeafVisible = useCallback(
    (leafKey: string) => setLeafVisible(leafKey, !isLeafVisible(leafKey)),
    [setLeafVisible, isLeafVisible]
  );

  /** The group's own visibility. Independent of the children: toggling a group
   * off and back on restores each layer's own eye state. */
  const isGroupVisible = useCallback(
    (groupKey: string) => groupVisibility[groupKey] !== false,
    [groupVisibility]
  );

  const toggleGroupVisible = useCallback(
    (groupKey: string) => {
      setGroupVisibility(prev => ({ ...prev, [groupKey]: prev[groupKey] === false }));
    },
    [setGroupVisibility]
  );

  /** Whether the layer actually renders: its group AND its own eye are on. */
  const isLeafShown = useCallback(
    (leafKey: string, groupKey: string) => isGroupVisible(groupKey) && isLeafVisible(leafKey),
    [isGroupVisible, isLeafVisible]
  );

  const groupOpacityValue = useCallback(
    (groupKey: string) => clamp01(groupOpacity[groupKey] ?? 1),
    [groupOpacity]
  );

  const setGroupOpacityValue = useCallback(
    (groupKey: string, value: number) => {
      setGroupOpacity(prev => ({ ...prev, [groupKey]: clamp01(value) }));
    },
    [setGroupOpacity]
  );

  const leafOpacityValue = useCallback(
    (leafKey: string) => clamp01(leafOpacity[leafKey] ?? 1),
    [leafOpacity]
  );

  const setLeafOpacityValue = useCallback(
    (leafKey: string, value: number) => {
      setLeafOpacity(prev => ({ ...prev, [leafKey]: clamp01(value) }));
    },
    [setLeafOpacity]
  );

  // Swipe membership is evaluated over the PRESENT event leaves only.
  const isInSwipe = useCallback((leafKey: string) => swipeLayers[leafKey] !== false, [swipeLayers]);

  const swipeIncludedKeys = useMemo(
    () => presentEventLeaves.filter(l => swipeLayers[l.key] !== false).map(l => l.key),
    [presentEventLeaves, swipeLayers]
  );

  const swipeAllSelected =
    presentEventLeaves.length > 0 && swipeIncludedKeys.length === presentEventLeaves.length;

  /** Set every PRESENT event leaf to a single membership state. */
  const setAllSwipe = useCallback(
    (included: boolean) => {
      setSwipeLayers(prev => {
        const next = { ...prev };
        for (const leaf of presentEventLeaves) {
          next[leaf.key] = included;
        }
        return next;
      });
    },
    [setSwipeLayers, presentEventLeaves]
  );

  const toggleSwipe = useCallback(
    (leafKey: string) => {
      setSwipeLayers(prev => ({ ...prev, [leafKey]: prev[leafKey] === false }));
    },
    [setSwipeLayers]
  );

  /** Toggle the whole present event set: deselect all if all are already in,
   * otherwise select all. */
  const toggleAllSwipe = useCallback(() => {
    setAllSwipe(!swipeAllSelected);
  }, [setAllSwipe, swipeAllSelected]);

  return {
    presentGroups,
    presentEventLeaves,
    // visibility
    isLeafVisible,
    toggleLeafVisible,
    isGroupVisible,
    toggleGroupVisible,
    isLeafShown,
    // opacity
    groupOpacityValue,
    setGroupOpacityValue,
    leafOpacityValue,
    setLeafOpacityValue,
    // swipe membership
    isInSwipe,
    swipeAllSelected,
    toggleAllSwipe,
    toggleSwipe,
  };
}
